import { useChartStore } from "@/stores/chartStore";
import type {
	BirthRecordDTO,
	BirthRecordEventDTO,
	BirthRecordLite,
	ChartSlotData,
} from "@/types/BirthRecord";

// Re-exported for existing consumers (DataBankPanel); the type itself lives in
// types/BirthRecord.ts so the store can use it without importing from hooks.
export type { ChartSlotData } from "@/types/BirthRecord";

export const slotOf = (d: ChartSlotData): ChartSlotData => ({
	year: d.year,
	month: d.month,
	day: d.day,
	hour: d.hour,
	min: d.min,
	place: d.place,
	latitude: d.latitude,
	longitude: d.longitude,
});

// Field-by-field slot equality. Inlined here (rather than importing `sameSlot`
// from useChartIdentity, which imports THIS module) to avoid a require cycle.
const sameSlotData = (a: ChartSlotData, b: ChartSlotData): boolean =>
	a.year === b.year &&
	a.month === b.month &&
	a.day === b.day &&
	a.hour === b.hour &&
	a.min === b.min &&
	a.place === b.place &&
	a.latitude === b.latitude &&
	a.longitude === b.longitude;

/**
 * Bridges saved records/events and the chartStore birth/present slots. Loading
 * rebuilds the form `Date` with the LOCAL constructor — the exact inverse of
 * `cvtDateToJson` — and sets `*DateTimeInfo` from the record's wall-clock ints
 * verbatim, so a save → load round-trip recomputes the identical chart and no
 * timezone drift (nor the persisted-Date ISO rehydration) can creep in. Reads
 * come from `*DateTimeInfo` (the computed ints), never the persisted `Date`.
 *
 * Every load also records WHERE the slot came from (`*RecordRef` + `recents`),
 * which drives the identity chip, the panel's "Loaded" highlight, and the
 * pre-compute recents strip.
 */
export function useDataBankSlots() {
	const birthDateTimeInfo = useChartStore((s) => s.birthDateTimeInfo);
	const place = useChartStore((s) => s.place);
	const latitude = useChartStore((s) => s.latitude);
	const longitude = useChartStore((s) => s.longitude);
	const presentDateTimeInfo = useChartStore((s) => s.presentDateTimeInfo);
	const presentPlace = useChartStore((s) => s.presentPlace);
	const presentLatitude = useChartStore((s) => s.presentLatitude);
	const presentLongitude = useChartStore((s) => s.presentLongitude);

	const currentChart: ChartSlotData | null = birthDateTimeInfo
		? {
				year: birthDateTimeInfo.year,
				month: birthDateTimeInfo.month,
				day: birthDateTimeInfo.day,
				hour: birthDateTimeInfo.hour,
				min: birthDateTimeInfo.min,
				place,
				latitude,
				longitude,
		  }
		: null;

	const currentTransit: ChartSlotData | null = presentDateTimeInfo
		? {
				year: presentDateTimeInfo.year,
				month: presentDateTimeInfo.month,
				day: presentDateTimeInfo.day,
				hour: presentDateTimeInfo.hour,
				min: presentDateTimeInfo.min,
				place: presentPlace,
				latitude: presentLatitude,
				longitude: presentLongitude,
		  }
		: null;

	// Raw birth-slot load — writes the record into the form + refs with no
	// dirty-guard. Accepts a lite record (id + name + slot fields) so cached
	// recents entries load without a fetch; a full BirthRecordDTO qualifies too.
	// Used by the guarded loader below, the discard-edits confirm, and moment
	// loads that must also pull in their parent chart.
	const applyRecordToBirth = (rec: BirthRecordLite) => {
		const s = useChartStore.getState();
		s.setBirthDateTime(new Date(rec.year, rec.month - 1, rec.day, rec.hour, rec.min));
		s.setLatitude(rec.latitude);
		s.setLongitude(rec.longitude);
		s.setPlace(rec.place);
		s.setBirthDateTimeInfo({
			year: rec.year,
			month: rec.month,
			day: rec.day,
			hour: rec.hour,
			min: rec.min,
		});
		s.setChartLabel(rec.name);
		// A moment label belongs to the previous chart's transit flow — a new
		// person starts with a blank one so a stale label can't ride along.
		s.setMomentLabel("");
		s.setBirthRecordRef({ id: rec.id, name: rec.name, snapshot: slotOf(rec) });
		s.pushRecent({ id: rec.id, name: rec.name, data: slotOf(rec), at: Date.now() });
		// Re-project tracked halfsums onto the loaded chart once it recomputes —
		// the store owns this signal, so no caller has to remember a refresh hop.
		s.bumpHalfsumRefresh();
	};

	// Guarded entry every "load into the birth slot" path calls (recents strip,
	// panel row, pre-compute dialog). If the birth chart has unsaved edits, stash
	// the load and let the discard-edits dialog confirm before clobbering them;
	// otherwise load straight away.
	const loadRecordIntoBirth = (rec: BirthRecordLite) => {
		const s = useChartStore.getState();
		const ref = s.birthRecordRef;
		const current: ChartSlotData | null = s.birthDateTimeInfo
			? {
					year: s.birthDateTimeInfo.year,
					month: s.birthDateTimeInfo.month,
					day: s.birthDateTimeInfo.day,
					hour: s.birthDateTimeInfo.hour,
					min: s.birthDateTimeInfo.min,
					place: s.place,
					latitude: s.latitude,
					longitude: s.longitude,
			  }
			: null;
		const dirty = !!ref && !!current && !sameSlotData(current, ref.snapshot);
		if (dirty) {
			s.setPendingBirthLoad(rec);
			return;
		}
		applyRecordToBirth(rec);
	};

	const applyToPresent = (d: BirthRecordDTO | BirthRecordEventDTO) => {
		const s = useChartStore.getState();
		s.setPresentDateTime(new Date(d.year, d.month - 1, d.day, d.hour, d.min));
		s.setPresentLatitude(d.latitude);
		s.setPresentLongitude(d.longitude);
		s.setPresentPlace(d.place);
		s.setPresentDateTimeInfo({
			year: d.year,
			month: d.month,
			day: d.day,
			hour: d.hour,
			min: d.min,
		});
	};

	const loadRecordIntoPresent = (rec: BirthRecordDTO) => {
		applyToPresent(rec);
		const s = useChartStore.getState();
		s.setPresentRecordRef({ id: rec.id, name: rec.name, snapshot: slotOf(rec) });
		s.bumpHalfsumRefresh();
		// Deliberately NOT pushed into recents: the recents strip loads into the
		// BIRTH slot, so a chart only ever used as Chart 2/transit must not
		// become a one-click overwrite of Chart 1.
	};

	const loadEventIntoPresent = (rec: BirthRecordDTO, ev: BirthRecordEventDTO) => {
		const s = useChartStore.getState();
		// Reconstruct the saved reading: a moment belongs to a specific person, so
		// if it isn't the chart currently in the birth slot, load that person too —
		// otherwise "load this bookmark" would pair their moment with whoever
		// happened to be on the dial. Uses the raw loader (this is one deliberate
		// action, not a user-initiated overwrite that needs the dirty guard).
		if (s.birthRecordRef?.id !== rec.id) applyRecordToBirth(rec);
		applyToPresent(ev);
		useChartStore.getState().setPresentRecordRef({
			id: rec.id,
			name: rec.name,
			eventId: ev.id,
			eventLabel: ev.label,
			snapshot: slotOf(ev),
		});
		useChartStore.getState().bumpHalfsumRefresh();
	};

	return {
		currentChart,
		currentTransit,
		loadRecordIntoBirth,
		// The raw, unguarded birth loader — the discard-edits confirm calls this
		// after the user chooses to drop their unsaved changes.
		forceLoadRecordIntoBirth: applyRecordToBirth,
		loadRecordIntoPresent,
		loadEventIntoPresent,
	};
}
